<template>
  <b-container fluid>
    <b-row>
      <b-col cols="12">
        <h5>
          <!--Inst icons in separate component -->
          <inst-icons :instAT="instrument.asset_type"></inst-icons>
          {{instrument.instrument_name}}
          <sup
            :title="instrument.harvest_flag === true
              ? 'Cloud data available.'
              : 'Currently not in Interactive Oceans systems.'"
            :class="instrument.harvest_flag === false
              ? 'text-muted'
              : ''"
          >
            <i class="fas fa-cloud"></i>
          </sup>
        </h5>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="6">
        <div class="instrument-attribute">
          <strong>Description: </strong>
          {{instrument.description}}
        </div>
      </b-col>
      <!--Status indicator text using nereus information-->
      <b-col cols="6">
        <div class="instrument-attribute">
          <strong>Status: </strong>
          <status-text :instRD="instrument.reference_designator"></status-text>
        </div>
      </b-col>
      <b-col cols="6">
        <div class="instrument-attribute">
          <strong>Reference Designator: </strong>
          {{instrument.reference_designator}}
        </div>
      </b-col>
      <b-col cols="3">
        <div class="instrument-attribute">
          <strong>Latitude: </strong>
          {{instrument.lat}}
        </div>
      </b-col>
      <b-col cols="3">
        <div class="instrument-attribute">
          <strong>Longitude: </strong>
          {{instrument.lon}}
        </div>
      </b-col>
      <b-col cols="6">
        <div class="instrument-attribute">
          <strong>Model: </strong>
          {{instrument.model ? instrument.model : "N/A"}}
        </div>
      </b-col>
      <b-col cols="6">
        <div class="instrument-attribute">
          <strong>Manufacturer: </strong>
          {{instrument.manufacturer ? instrument.manufacturer : "N/A"}}
        </div>
      </b-col>
      <b-col cols="6">
        <div class="instrument-attribute">
          <strong>Owner: </strong>
          {{instrument.owner ? instrument.owner : "N/A"}}
        </div>
      </b-col>
    </b-row>
    <b-row class="pt-3">
      <b-col cols="12">
        <b-tabs content-class="mt-3" fill>
          <b-tab>
            <template v-slot:title>
              Deployments
              <b-badge pill variant="info">{{deployments.length}}</b-badge>
              <b-spinner v-if="loadingDeployments" class="ml-1" type="border" small></b-spinner>
            </template>
            <p v-if="deployments.length === 0" class="text-center">
              <strong>No deployments found.</strong>
            </p>
            <b-table
              v-if="deployments.length > 0"
              hover
              :items="deployments"
              small
              fixed stacked>
            </b-table>
          </b-tab>
          <b-tab>
            <template v-slot:title>
              Annotations
              <b-badge pill variant="info">{{annotations.length}}</b-badge>
              <b-spinner v-if="loadingAnnotations" class="ml-1" type="border" small></b-spinner>
            </template>
            <p v-if="annotations.length === 0" class="text-center">
              <strong>No annotations found.</strong>
            </p>
            <b-table
              v-if="annotations.length > 0"
              hover
              :items="annotations"
              small
              fixed
              stacked>
            </b-table>
          </b-tab>
        </b-tabs>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import _ from 'lodash';
import { DateTime } from 'luxon';
import axios from 'axios';
import { APIBASEURL, APIMETADATA } from '@/utils/constants';
import InstIconVue from './instIcons';
import StatusIndicatorTextVue from '../statusIndicators/StatusIndicatorText';

export default {
  props: ['refdes', 'instrument', 'parameters'],
  data() {
    return {
      deployments: [],
      annotations: [],
      loadingDeployments: false,
      loadingAnnotations: false,
    };
  },
  components: {
    'inst-icons': InstIconVue,
    'status-text': StatusIndicatorTextVue,
  },
  mounted() {
    this.fetchDeployments();
    this.fetchAnnotations();
  },
  methods: {
    processDeployments(deployments) {
      const sortedDeployments = _.reverse(
        _.sortBy(deployments, ['deployment_number']),
      );
      this.deployments = sortedDeployments.map((dep) => {
        const newDep = {
          number: dep.deployment_number,
          UID: dep.uid,
          start: DateTime.fromMillis(dep.deployment_start)
            .toUTC()
            .toLocaleString(DateTime.DATETIME_FULL),
          end:
            dep.deployment_end !== null
              ? DateTime.fromMillis(dep.deployment_end)
                .toUTC()
                .toLocaleString(DateTime.DATETIME_FULL)
              : 'Ongoing',
          location: `(${dep.lat}, ${dep.lon})`,
        };
        return newDep;
      });
    },
    processAnnotations(annotations) {
      // const filtAnnotations = _.filter(annotations, (o) => {
      //   const refList = [o.site_rd, o.infrastructure_rd, o.instrument_rd];
      //   return _.indexOf(refList, null) === -1;
      // });
      // this.annotations = filtAnnotations.map((anno) => {
      this.annotations = annotations.map((anno) => {
        const newRefList = [
          anno.site_rd,
          anno.infrastructure_rd,
          anno.instrument_rd,
          anno.stream_method,
          anno.stream_rd,
        ];
        const newAnno = {
          annotation_ID: anno.id,
          reference: _.join(
            _.remove(newRefList, (r) => r !== null),
            '-',
          ),
          start:
            anno.begin_date !== null
              ? DateTime.fromMillis(anno.begin_date)
                .toUTC()
                .toLocaleString(DateTime.DATE_FULL)
              : 'N/A',
          end:
            anno.end_date !== null
              ? DateTime.fromMillis(anno.end_date)
                .toUTC()
                .toLocaleString(DateTime.DATE_FULL)
              : 'N/A',
          comment: anno.annotation,
          author: anno.source,
          parameters:
            anno.parameters.length > 0 ? _.join(anno.parameters, ', ') : 'N/A',
          QC_flag: anno.qc_flag !== null ? _.startCase(anno.qc_flag) : 'N/A',
          exclude: anno.exclusion_flag ? 'Yes' : 'No',
        };
        return newAnno;
      });
    },
    fetchDeployments() {
      this.loadingDeployments = true;
      const params = {
        refdes: this.refdes,
      };
      axios
        .get(`${APIBASEURL}/${APIMETADATA}/get_deployments`, { params })
        .then((resp) => {
          this.processDeployments(resp.data);
          this.loadingDeployments = false;
        });
    },
    fetchAnnotations() {
      this.loadingAnnotations = true;
      const params = {
        ref: this.refdes,
        start_dt: DateTime.utc(2014, 1, 1, 0, 0, 0).toISO(),
        end_dt: DateTime.utc().toISO(),
      };
      axios
        .get(`${APIBASEURL}/${APIMETADATA}/get_annotations`, { params })
        .then((resp) => {
          this.processAnnotations(resp.data.annotations[this.refdes]);
          this.loadingAnnotations = false;
        });
    },
  },
};
</script>

<style scoped>
.instrument-attribute {
  background-color: rgba(211, 211, 211, 0.3);
  border-radius: 0.5rem;
  margin: 0.3rem 0 0.3rem 0;
  padding: 0.7rem;
}
</style>
